import React, { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { KeyRound, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { inviteService } from '../services/InviteService';

/**
 * Join screen — a signed-in user who isn't attached to a facility enters an
 * invite code (or arrives via a link with ?code=) to be provisioned into that
 * facility. The company-gate in ProtectedRoute routes company-less users here.
 * Only active when REACT_APP_INVITE_ONBOARDING=true.
 */
export const Join: React.FC = () => {
  const { user, signOut } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get('code') || '').toUpperCase());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Already provisioned → nothing to do here.
  if (user?.companyId) return <Navigate to="/dashboard" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Enter the invite code your facility gave you.');
      return;
    }
    setStatus('submitting');
    setError(null);
    try {
      await inviteService.redeemInvite(code);
      // Re-fetch the user (now attached to a facility) and drop into the app.
      window.location.reload();
    } catch (err: any) {
      setStatus('error');
      setError(err?.message || 'That code could not be redeemed. Check it and try again.');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        backgroundColor: '#f4f2ef',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #e5e0db',
          padding: 28,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: '#4F6367',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <KeyRound size={20} color="#fff" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2D2D2D', margin: 0 }}>Join your facility</h1>
        </div>
        <p style={{ color: '#6b6b6b', fontSize: 14, lineHeight: 1.6, marginTop: 8 }}>
          Enter the invite code your facility administrator gave you to finish
          setting up your account.
        </p>

        <form onSubmit={submit} style={{ marginTop: 16 }}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="INVITE CODE"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            style={{
              width: '100%',
              padding: '12px 14px',
              fontSize: 18,
              letterSpacing: 2,
              textAlign: 'center',
              fontFamily: 'monospace',
              border: '1px solid #E5E0DB',
              borderRadius: 10,
              boxSizing: 'border-box',
            }}
          />

          {error && (
            <div
              role="alert"
              style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontSize: 13, marginTop: 10 }}
            >
              <AlertTriangle size={15} /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            style={{
              marginTop: 16,
              width: '100%',
              padding: '12px 16px',
              background: '#4F6367',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
              opacity: status === 'submitting' ? 0.6 : 1,
            }}
          >
            {status === 'submitting' ? 'Joining…' : 'Join'}
          </button>
        </form>

        <div style={{ marginTop: 18, fontSize: 13, color: '#6b6b6b', textAlign: 'center' }}>
          Signed in as {user?.email || 'your account'} ·{' '}
          <button
            type="button"
            onClick={() => signOut()}
            style={{ background: 'none', border: 'none', color: '#4F6367', fontWeight: 600, cursor: 'pointer', padding: 0 }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Join;
